
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Package, Image as ImageIcon, X, Star, Trash2, Save, DollarSign, Box, Info, Truck, Settings, Plus, Edit2, Palette, Ruler } from "lucide-react";
import { Link } from "react-router-dom";
import { adminApi, categoriesApi, collectionsApi } from "@/lib/api";
import { useAuthStore } from "@/lib/store";
import toast from "react-hot-toast";
import { uploadFileToS3, generateProductImageKey, deleteFileFromS3, extractS3KeyFromUrl, getS3BucketName } from "@/lib/s3";
import ImageGallery, { useImageGallery } from "../../components/common/ImageGallery";
// Image removed - use img tag;

interface ProductImage {
  id: number;
  image_url: string;
  alt_text: string | null;
  is_primary: boolean;
  display_order: number;
}

interface VariantImage {
  id: number;
  variant_id: number;
  image_url: string;
  alt_text: string | null;
  is_primary: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

interface ProductVariant {
  id: number;
  product_id: number;
  color: string;
  size: string | null;
  sku: string | null;
  price: number | null;
  stock: number;
  image_url: string | null;  // Deprecated, use images array
  is_active: boolean;
  display_order: number;
  images?: VariantImage[];  // Array of variant images
}

export default function ProductDetailsPage() {
  const params = useParams();
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const productId = parseInt(params.id as string);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const { isOpen, initialIndex, openGallery, closeGallery } = useImageGallery();
  const [galleryImages, setGalleryImages] = useState<Array<{ id: string | number; url: string; alt?: string }>>([]);

  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState<ProductImage[]>([]);
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [collections, setCollections] = useState<any[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<{ id: number; url: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showVariantDialog, setShowVariantDialog] = useState(false);
  const [editingVariant, setEditingVariant] = useState<ProductVariant | null>(null);
  const [variantFormData, setVariantFormData] = useState({
    color: "",
    size: "",
    sku: "",
    price: "",
    stock: "0",
    image_url: "",
    is_active: true,
    display_order: "0",
  });
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    price: "",
    original_price: "",
    buyoutprice: "",
    stock: "",
    collection_id: "",
    category_id: "",
    is_active: true,
    dimensions: "",
    care_tips: "",
    shipping_info: "",
    style_tip: "",
    tax_included: false,
    cod_available: false,
    returns_available: true,
    express_delivery_available: false,
    free_shipping_threshold: "500",
    quality_description: "",
    is_price_frozen: false,
  });

  useEffect(() => {
    if (token && productId) {
      fetchProduct();
      fetchCategoriesAndCollections();
      fetchVariants();
    }
  }, [token, productId]);

  const fetchCategoriesAndCollections = async () => {
    try {
      const [categoriesData, collectionsData] = await Promise.all([
        categoriesApi.getAll(),
        collectionsApi.getAll(),
      ]);
      setCategories(categoriesData.categories || categoriesData || []);
      setCollections(collectionsData.collections || collectionsData || []);
    } catch (error) {
      console.error("Error fetching categories/collections:", error);
    }
  };

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getProduct(productId, token!);
      setProduct(data);
      setImages(data.images || []);
      
      // Initialize form data from product
      setFormData({
        name: data.name || "",
        slug: data.slug || "",
        description: data.description || "",
        price: data.price?.toString() || "",
        original_price: data.original_price?.toString() || "",
        buyoutprice: data.buyoutprice?.toString() || "",
        stock: data.stock?.toString() || "",
        collection_id: data.collection_id?.toString() || "",
        category_id: data.category_id?.toString() || "",
        is_active: data.is_active ?? true,
        dimensions: data.dimensions || "",
        care_tips: data.care_tips || "",
        shipping_info: data.shipping_info || "",
        style_tip: data.style_tip || "",
        tax_included: data.tax_included ?? false,
        cod_available: data.cod_available ?? false,
        returns_available: data.returns_available ?? true,
        express_delivery_available: data.express_delivery_available ?? false,
        free_shipping_threshold: data.free_shipping_threshold?.toString() || "500",
        quality_description: data.quality_description || "",
        is_price_frozen: data.is_price_frozen ?? false,
      });
    } catch (error: any) {
      console.error("Error fetching product:", error);
      toast.error(error.response?.data?.detail || "Failed to load product");
      navigate("/admin/products");
    } finally {
      setLoading(false);
    }
  };

  const fetchVariants = async () => {
    if (!token) return;
    try {
      const data = await adminApi.getProductVariants(productId, token);
      // Ensure data is always an array
      const variantsArray = Array.isArray(data) ? data : (data?.variants || data?.data || []);
      setVariants(variantsArray);
    } catch (error: any) {
      console.error("Error fetching variants:", error);
      // Set to empty array on error to prevent map errors
      setVariants([]);
      // Don't show error toast, variants might not exist yet
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !token) return;

    setUploading(true);
    try {
      // Get S3 bucket name from environment variables
      const bucketName = getS3BucketName();

      const filesArray = Array.from(files);
      let successCount = 0;

      // Upload each file sequentially to avoid overwhelming the server
      for (const file of filesArray) {
        try {
          // Validate file type
          if (!file.type.startsWith("image/")) {
            toast.error(`${file.name} is not an image file`);
            continue;
          }

          // Validate file size (10MB max)
          const maxSize = 10 * 1024 * 1024; // 10MB
          if (file.size > maxSize) {
            toast.error(`${file.name} exceeds 10MB size limit`);
            continue;
          }

          // Generate S3 key for product image: products/{productId}/image_{timestamp}.{ext}
          const s3Key = generateProductImageKey(productId, file.name);

          // Upload file directly to S3
          const imageUrl = await uploadFileToS3(file, s3Key, bucketName);

          // Create ProductImage record in database
          await adminApi.addProductImage(productId, {
            image_url: imageUrl,
            alt_text: file.name,
            is_primary: images.length === 0 && successCount === 0, // First image is primary
            display_order: images.length + successCount,
          }, token);

          successCount++;
        } catch (error: any) {
          console.error(`Error uploading ${file.name}:`, error);
          toast.error(`Failed to upload ${file.name}: ${error.message}`);
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully uploaded ${successCount} image(s)`);
      // Refresh product data to get updated images
      await fetchProduct();
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      toast.error(error.message || "Failed to upload images");
    } finally {
      setUploading(false);
      // Reset file input
      if (e.target) {
        e.target.value = "";
      }
    }
  };

  const handleDeleteImage = (imageId: number, imageUrl: string) => {
    setImageToDelete({ id: imageId, url: imageUrl });
    setShowDeleteDialog(true);
  };

  const confirmDeleteImage = async () => {
    if (!token || !imageToDelete) return;

    setDeleting(true);
    try {
      // Get S3 bucket name from environment variables
      const bucketName = getS3BucketName();

      // Extract S3 key from image URL
      const s3Key = extractS3KeyFromUrl(imageToDelete.url);
      
      // Delete from S3 first
      if (s3Key) {
        try {
          await deleteFileFromS3(s3Key, bucketName);
        } catch (s3Error: any) {
          console.error("Error deleting from S3:", s3Error);
          // Continue with backend deletion even if S3 deletion fails
          // (the file might already be deleted or not exist)
          toast.error(`S3 deletion warning: ${s3Error.message}. Continuing with database deletion...`);
        }
      } else {
        console.warn("Could not extract S3 key from URL:", imageToDelete.url);
        // Continue with backend deletion even if we can't extract the S3 key
      }

      // Delete from backend database
      await adminApi.deleteProductImage(productId, imageToDelete.id, token);
      toast.success("Image deleted successfully");
      await fetchProduct();
      
      // Close dialog
      setShowDeleteDialog(false);
      setImageToDelete(null);
    } catch (error: any) {
      console.error("Error deleting image:", error);
      toast.error(error.response?.data?.detail || error.message || "Failed to delete image");
    } finally {
      setDeleting(false);
    }
  };

  const handleSetPrimary = async (imageId: number) => {
    if (!token) return;

    try {
      await adminApi.setPrimaryImage(productId, imageId, token);
      toast.success("Primary image updated");
      await fetchProduct();
    } catch (error: any) {
      console.error("Error setting primary image:", error);
      toast.error(error.response?.data?.detail || "Failed to update primary image");
    }
  };

  // Variant Management Functions
  const openVariantDialog = (variant?: ProductVariant) => {
    if (variant) {
      setEditingVariant(variant);
      setVariantFormData({
        color: variant.color || "",
        size: variant.size || "",
        sku: variant.sku || "",
        price: variant.price?.toString() || "",
        stock: variant.stock?.toString() || "0",
        image_url: variant.image_url || "",
        is_active: variant.is_active,
        display_order: variant.display_order?.toString() || "0",
      });
    } else {
      setEditingVariant(null);
      setVariantFormData({
        color: "",
        size: "",
        sku: "",
        price: "",
        stock: "0",
        image_url: "",
        is_active: true,
        display_order: (Array.isArray(variants) ? variants.length : 0).toString(),
      });
    }
    setShowVariantDialog(true);
  };

  const closeVariantDialog = () => {
    setShowVariantDialog(false);
    setEditingVariant(null);
    setVariantFormData({
      color: "",
      size: "",
      sku: "",
      price: "",
      stock: "0",
      image_url: "",
      is_active: true,
      display_order: "0",
    });
  };

  const handleVariantSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    try {
      const variantData: any = {
        color: variantFormData.color,
        size: variantFormData.size || null,
        sku: variantFormData.sku || null,
        price: variantFormData.price ? parseFloat(variantFormData.price) : null,
        stock: parseInt(variantFormData.stock) || 0,
        image_url: variantFormData.image_url || null,
        is_active: variantFormData.is_active,
        display_order: parseInt(variantFormData.display_order) || 0,
      };

      if (editingVariant) {
        await adminApi.updateProductVariant(productId, editingVariant.id, variantData, token);
        toast.success("Variant updated successfully");
      } else {
        await adminApi.createProductVariant(productId, variantData, token);
        toast.success("Variant created successfully");
      }
      
      await fetchVariants();
      closeVariantDialog();
    } catch (error: any) {
      console.error("Error saving variant:", error);
      toast.error(error.response?.data?.detail || "Failed to save variant");
    }
  };

  const handleDeleteVariant = async (variantId: number) => {
    if (!token) return;
    if (!confirm("Are you sure you want to delete this variant? This action cannot be undone.")) return;

    try {
      await adminApi.deleteProductVariant(productId, variantId, token);
      toast.success("Variant deleted successfully");
      await fetchVariants();
    } catch (error: any) {
      console.error("Error deleting variant:", error);
      toast.error(error.response?.data?.detail || "Failed to delete variant");
    }
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setFormData({ ...formData, name });
    if (!formData.slug || formData.slug === generateSlug(product?.name || "")) {
      setFormData((prev) => ({ ...prev, slug: generateSlug(name) }));
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : type === "number" ? value : value,
    });
  };

  const handleTogglePriceFrozen = async () => {
    if (!token) return;
    try {
      await adminApi.togglePriceFrozen(productId, token);
      toast.success(`Price ${formData.is_price_frozen ? 'unfrozen' : 'frozen'} successfully`);
      // Refresh product data to get updated status
      await fetchProduct();
    } catch (error: any) {
      console.error("Error toggling price frozen status:", error);
      toast.error(error.response?.data?.detail || "Failed to update price frozen status");
    }
  };

  const handleSaveChanges = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      toast.error("Authentication required");
      return;
    }

    setSaving(true);
    try {
      const productData: any = {};
      
      // Only include fields that have values or are explicitly set
      if (formData.name) productData.name = formData.name;
      if (formData.slug) productData.slug = formData.slug;
      if (formData.description !== undefined) productData.description = formData.description || null;
      if (formData.price) productData.price = parseFloat(formData.price);
      if (formData.original_price) productData.original_price = parseFloat(formData.original_price) || null;
      if (formData.buyoutprice) productData.buyoutprice = parseFloat(formData.buyoutprice) || null;
      if (formData.stock !== undefined) productData.stock = parseInt(formData.stock) || 0;
      if (formData.collection_id) productData.collection_id = parseInt(formData.collection_id) || null;
      if (formData.category_id) productData.category_id = parseInt(formData.category_id) || null;
      productData.is_active = formData.is_active;
      if (formData.dimensions !== undefined) productData.dimensions = formData.dimensions || null;
      if (formData.care_tips !== undefined) productData.care_tips = formData.care_tips || null;
      if (formData.shipping_info !== undefined) productData.shipping_info = formData.shipping_info || null;
      if (formData.style_tip !== undefined) productData.style_tip = formData.style_tip || null;
      productData.tax_included = formData.tax_included;
      productData.cod_available = formData.cod_available;
      productData.returns_available = formData.returns_available;
      productData.express_delivery_available = formData.express_delivery_available;
      if (formData.free_shipping_threshold) {
        productData.free_shipping_threshold = parseFloat(formData.free_shipping_threshold) || null;
      }
      if (formData.quality_description !== undefined) productData.quality_description = formData.quality_description || null;
      productData.is_price_frozen = formData.is_price_frozen;

      await adminApi.updateProduct(productId, productData, token);
      toast.success("Product updated successfully!");
      await fetchProduct();
    } catch (error: any) {
      console.error("Error updating product:", error);
      toast.error(error.response?.data?.detail || "Failed to update product");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="h-6 w-32 bg-gray-200 rounded-lg animate-pulse mb-4" />
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="h-64 bg-gray-200 rounded-lg animate-pulse" />
        </div>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <div className="p-4">
      {/* Header */}
      <div className="mb-4">
        <Link
          to="/admin/products"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-aarsa-sage-600 mb-3 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Products
        </Link>
        <h1 className="text-xl font-bold font-playfair mb-0.5">{product.name}</h1>
        <p className="text-xs text-gray-500">
          Product ID: {product.id} | Code: {product.product_code || "—"} | Slug: {product.slug}
        </p>
      </div>

      <div className="space-y-4">
          {/* Product Images Section */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-3">
                <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                  <ImageIcon className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-sm font-bold text-gray-900">Product Images</h2>
              </div>
            </div>
            <div className="p-4 space-y-4">
              {/* Upload Area */}
              <div
                className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
                  uploading
                    ? "border-aarsa-sage-400 bg-aarsa-sage-50 cursor-not-allowed"
                    : "border-gray-300 hover:border-aarsa-sage-500 hover:bg-aarsa-sage-50"
                }`}
              >
                <input
                  type="file"
                accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  disabled={uploading}
                  className="hidden"
                  id="product-image-upload"
                />
                <label
                  htmlFor="product-image-upload"
                  className={`cursor-pointer ${uploading ? "cursor-not-allowed" : ""}`}
                >
                  {uploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-8 h-8 border-4 border-aarsa-sage-600 border-t-transparent rounded-full animate-spin" />
                      <p className="text-sm text-gray-600">Uploading images...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <div className="p-3 rounded-full bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                        <ImageIcon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-700">
                          Click to upload or drag and drop
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Images up to 10MB (Max 10 files)
                        </p>
                      </div>
                    </div>
                  )}
                </label>
              </div>

              {/* Existing Images */}
              {images.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-700 mb-3">Uploaded Images</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {images.map((img, index) => (
                      <div
                        key={img.id}
                        className="relative group aspect-square rounded-lg overflow-hidden border-2 border-gray-200 hover:border-aarsa-sage-300 transition-all cursor-pointer"
                        onClick={(e) => {
                          // Don't open gallery if clicking on buttons
                          if ((e.target as HTMLElement).closest('button')) {
                            return;
                          }
                          const galleryImagesData = images.map((i) => ({
                            id: i.id,
                            url: i.image_url,
                            alt: i.alt_text || "Product image",
                          }));
                          setGalleryImages(galleryImagesData);
                          openGallery(index);
                        }}
                      >
                        <img
                          src={img.image_url}
                          alt={img.alt_text || "Product image"}
                          className="w-full h-full object-cover"
                        />
                        {img.is_primary && (
                          <div className="absolute top-2 left-2 bg-aarsa-sage-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1 z-10">
                            <Star className="w-3 h-3 -current" />
                            Primary
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                          {!img.is_primary && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSetPrimary(img.id);
                              }}
                              className="px-3 py-1.5 bg-gray-900 text-white text-xs rounded hover:bg-gray-800 transition-colors z-10"
                            >
                              Set Primary
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteImage(img.id, img.image_url);
                            }}
                            className="px-3 py-1.5 bg-black text-white text-xs rounded hover:bg-gray-900 transition-colors z-10"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Product Variants Section */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                    <Palette className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-sm font-bold text-gray-900">Product Variants (Color & Size)</h2>
                </div>
                <button
                  type="button"
                  onClick={() => openVariantDialog()}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white text-xs font-semibold rounded-lg hover:bg-gray-800 transition-all"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Variant
                </button>
              </div>
            </div>
            <div className="p-4">
              {!Array.isArray(variants) || variants.length === 0 ? (
                <div className="text-center py-8">
                  <Palette className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500 mb-2">No variants added yet</p>
                  <p className="text-xs text-gray-400">Click "Add Variant" to create color and size options for this product</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 font-semibold text-gray-700">Color</th>
                        <th className="text-left py-2 px-3 font-semibold text-gray-700">Size</th>
                        <th className="text-left py-2 px-3 font-semibold text-gray-700">SKU</th>
                        <th className="text-left py-2 px-3 font-semibold text-gray-700">Price</th>
                        <th className="text-left py-2 px-3 font-semibold text-gray-700">Stock</th>
                        <th className="text-left py-2 px-3 font-semibold text-gray-700">Status</th>
                        <th className="text-right py-2 px-3 font-semibold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(variants) && variants.map((variant) => (
                        <tr key={variant.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-2.5 px-3">
                            <div className="flex items-center gap-2">
                              {variant.image_url && (
                                <img
                                  src={variant.image_url}
                                  alt={variant.color}
                                  className="w-8 h-8 rounded border border-gray-200 object-cover"
                                />
                              )}
                              <span className="font-medium text-gray-900">{variant.color}</span>
                            </div>
                          </td>
                          <td className="py-2.5 px-3 text-gray-600">{variant.size || "-"}</td>
                          <td className="py-2.5 px-3 text-gray-600 font-mono text-xs">{variant.sku || "-"}</td>
                          <td className="py-2.5 px-3 text-gray-600">
                            {variant.price ? `₹${variant.price.toLocaleString()}` : "Default"}
                          </td>
                          <td className="py-2.5 px-3">
                            <span className={`font-medium ${variant.stock > 0 ? "text-gray-900" : "text-red-600"}`}>
                              {variant.stock}
                            </span>
                          </td>
                          <td className="py-2.5 px-3">
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                variant.is_active
                                  ? "bg-green-100 text-green-700"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {variant.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="py-2.5 px-3">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => openVariantDialog(variant)}
                                className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                                title="Edit variant"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteVariant(variant.id)}
                                className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                title="Delete variant"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </motion.div>

          {/* Product Details Form */}
          <form onSubmit={handleSaveChanges}>
            <div className="space-y-3">
              {/* Category & Collection */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                    <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                      <Package className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-sm font-bold text-gray-900">Category & Collection</h2>
                  </div>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-3">
                        Category
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {categories.length === 0 ? (
                          <p className="text-xs text-gray-500">No categories available</p>
                        ) : (
                          categories.map((cat) => (
                            <button
                              key={cat.id}
                              type="button"
                              onClick={() => setFormData({ ...formData, category_id: String(cat.id) })}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                                formData.category_id === String(cat.id)
                                  ? "bg-black text-white shadow-sm"
                                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                              }`}
                            >
                              {cat.name}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-3">
                        Collection
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {collections.length === 0 ? (
                          <p className="text-xs text-gray-500">No collections available</p>
                        ) : (
                          collections.map((col) => (
                            <button
                              key={col.id}
                              type="button"
                              onClick={() => setFormData({ ...formData, collection_id: String(col.id) })}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                                formData.collection_id === String(col.id)
                                  ? "bg-black text-white shadow-sm"
                                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                              }`}
                            >
                              {col.title}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Row 1: Basic Information + Pricing */}
              <div className="grid grid-cols-2 gap-3">
                {/* Basic Information */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
                <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                    <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                        <Info className="w-4 h-4 text-white" />
                      </div>
                      <h2 className="text-sm font-bold text-gray-900">Basic Information</h2>
                    </div>
                  </div>
                  <div className="p-4 space-y-2.5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Product Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleNameChange}
                        required
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                        placeholder="Enter product name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Slug <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="slug"
                        value={formData.slug}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                        placeholder="product-slug"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Description</label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows={3}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent resize-none"
                        placeholder="Enter product description"
                      />
                    </div>
                  </div>
                </motion.div>

                {/* Pricing & Inventory */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                >
                <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                    <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                        <DollarSign className="w-4 h-4 text-white" />
                      </div>
                      <h2 className="text-sm font-bold text-gray-900">Pricing & Inventory</h2>
                    </div>
                  </div>
                  <div className="p-4 space-y-2.5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Price (₹) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={handleInputChange}
                        required
                        min="0"
                        step="0.01"
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Original Price (₹)
                      </label>
                      <input
                        type="number"
                        name="original_price"
                        value={formData.original_price}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Buyout Price (₹)
                      </label>
                      <input
                        type="number"
                        name="buyoutprice"
                        value={formData.buyoutprice}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Stock Quantity
                      </label>
                      <input
                        type="number"
                        name="stock"
                        value={formData.stock}
                        onChange={handleInputChange}
                        min="0"
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Row 2: Product Details + Shipping & Options */}
              <div className="grid grid-cols-2 gap-3">
                {/* Product Details */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                >
                <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                    <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                        <Box className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-sm font-bold text-gray-900">Product Details</h2>
              </div>
            </div>
                  <div className="p-4 space-y-2.5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Dimensions</label>
                      <input
                        type="text"
                        name="dimensions"
                        value={formData.dimensions}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                        placeholder="e.g., 23*8.5 cm, 115 g"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Quality Description</label>
                      <input
                        type="text"
                        name="quality_description"
                        value={formData.quality_description}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                        placeholder="Enter quality information"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Care Tips</label>
                      <textarea
                        name="care_tips"
                        value={formData.care_tips}
                        onChange={handleInputChange}
                        rows={2}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent resize-none"
                        placeholder="Enter care instructions"
                      />
                    </div>
                <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Style Tip</label>
                      <textarea
                        name="style_tip"
                        value={formData.style_tip}
                        onChange={handleInputChange}
                        rows={2}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent resize-none"
                        placeholder="Enter styling suggestions"
                      />
                </div>
                <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Shipping Info</label>
                      <textarea
                        name="shipping_info"
                        value={formData.shipping_info}
                        onChange={handleInputChange}
                        rows={2}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent resize-none"
                        placeholder="Enter shipping information"
                      />
                    </div>
                  </div>
                </motion.div>

                {/* Shipping & Options */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                >
                <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                    <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                        <Truck className="w-4 h-4 text-white" />
                      </div>
                      <h2 className="text-sm font-bold text-gray-900">Shipping & Options</h2>
                </div>
              </div>
                  <div className="p-4 space-y-2.5">
                <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Free Shipping Threshold (₹)
                      </label>
                      <input
                        type="number"
                        name="free_shipping_threshold"
                        value={formData.free_shipping_threshold}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                        placeholder="500.00"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          name="tax_included"
                          checked={formData.tax_included}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-aarsa-sage-600 rounded border-gray-300 focus:ring-2 focus:ring-aarsa-sage-500"
                        />
                        <span className="text-xs font-medium text-gray-700">Tax Included</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          name="cod_available"
                          checked={formData.cod_available}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-aarsa-sage-600 rounded border-gray-300 focus:ring-2 focus:ring-aarsa-sage-500"
                        />
                        <span className="text-xs font-medium text-gray-700">COD Available</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          name="returns_available"
                          checked={formData.returns_available}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-aarsa-sage-600 rounded border-gray-300 focus:ring-2 focus:ring-aarsa-sage-500"
                        />
                        <span className="text-xs font-medium text-gray-700">Returns Available</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          name="express_delivery_available"
                          checked={formData.express_delivery_available}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-aarsa-sage-600 rounded border-gray-300 focus:ring-2 focus:ring-aarsa-sage-500"
                        />
                        <span className="text-xs font-medium text-gray-700">Express Delivery</span>
                      </label>
                </div>
            </div>
          </motion.div>
        </div>

              {/* Status */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                    <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                      <Settings className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-sm font-bold text-gray-900">Status</h2>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-6 flex-wrap">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-aarsa-sage-600 rounded border-gray-300 focus:ring-2 focus:ring-aarsa-sage-500"
                      />
                      <span className="text-xs font-medium text-gray-700">Active</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleTogglePriceFrozen}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 ${
                          formData.is_price_frozen
                            ? "bg-blue-500"
                            : "bg-gray-300"
                        }`}
                        title={formData.is_price_frozen ? "Click to unfreeze price" : "Click to freeze price"}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            formData.is_price_frozen ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                      <span className="text-xs font-medium text-gray-700">
                        Price Frozen
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          formData.is_price_frozen
                            ? "bg-blue-100 text-blue-700"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {formData.is_price_frozen ? "Frozen" : "Unfrozen"}
                      </span>
                    </div>
              </div>
                </div>
              </motion.div>

              {/* Save Button */}
              <div className="flex justify-end gap-3 pt-2 pb-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
      </div>

      {/* Delete Image Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Image</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this image? This action cannot be undone. The image will be removed from both AWS S3 and the database.
            </p>
            {imageToDelete && (
              <div className="mb-6">
                <img
                  src={imageToDelete.url}
                  alt="Image to delete"
                  className="w-full h-32 object-cover rounded-lg border border-gray-200"
                />
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setImageToDelete(null);
                }}
                disabled={deleting}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteImage}
                disabled={deleting}
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-900 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete Image
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Variant Dialog */}
      {showVariantDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-aarsa-sage-100 rounded-full">
                  <Palette className="w-5 h-5 text-aarsa-sage-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingVariant ? "Edit Variant" : "Add New Variant"}
                </h3>
              </div>
              <button
                onClick={closeVariantDialog}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleVariantSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Color <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={variantFormData.color}
                    onChange={(e) => setVariantFormData({ ...variantFormData, color: e.target.value })}
                    required
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="e.g., Red, Blue, Black"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Size</label>
                  <input
                    type="text"
                    value={variantFormData.size}
                    onChange={(e) => setVariantFormData({ ...variantFormData, size: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="e.g., S, M, L, XL"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">SKU</label>
                  <input
                    type="text"
                    value={variantFormData.sku}
                    onChange={(e) => setVariantFormData({ ...variantFormData, sku: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent font-mono"
                    placeholder="e.g., PROD-RED-M"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Price (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={variantFormData.price}
                    onChange={(e) => setVariantFormData({ ...variantFormData, price: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="Leave empty for product price"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty to use product default price</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Stock <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={variantFormData.stock}
                    onChange={(e) => setVariantFormData({ ...variantFormData, stock: e.target.value })}
                    required
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Display Order</label>
                  <input
                    type="number"
                    min="0"
                    value={variantFormData.display_order}
                    onChange={(e) => setVariantFormData({ ...variantFormData, display_order: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Variant Image URL</label>
                <input
                  type="url"
                  value={variantFormData.image_url}
                  onChange={(e) => setVariantFormData({ ...variantFormData, image_url: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                  placeholder="https://example.com/image.jpg"
                />
                <p className="text-xs text-gray-500 mt-1">Optional: URL for variant-specific image</p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="variant-active"
                  checked={variantFormData.is_active}
                  onChange={(e) => setVariantFormData({ ...variantFormData, is_active: e.target.checked })}
                  className="w-4 h-4 text-aarsa-sage-600 rounded border-gray-300 focus:ring-2 focus:ring-aarsa-sage-500"
                />
                <label htmlFor="variant-active" className="text-xs font-medium text-gray-700 cursor-pointer">
                  Active (visible to customers)
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeVariantDialog}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium text-sm flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {editingVariant ? "Update Variant" : "Create Variant"}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Image Gallery */}
      {isOpen && galleryImages.length > 0 && (
        <ImageGallery
          images={galleryImages}
          initialIndex={initialIndex}
          isOpen={isOpen}
          onClose={closeGallery}
          showThumbnails={true}
        />
      )}
    </div>
  );
}
